import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import TooltipComponent, { tooltipClasses } from '@mui/material/Tooltip';
import { uuidV4 } from 'src/helper';
import { ClipboardController } from 'src/components/general';
import styles from './tooltipStyle.module.scss';

type TooltipProps = {
  children: ReactNode;
  text: string | string[] | null;
  disabled?: boolean;
  containerClassName?: string;
  tooltipClassName?: string;
  arrowClassName?: string;
  withClipboard?: boolean;
  onMount?: (elem: HTMLDivElement) => void;
};

export const Tooltip = ({
  children,
  text,
  disabled = false,
  containerClassName,
  tooltipClassName,
  arrowClassName,
  withClipboard = false,
  onMount,
}: TooltipProps) => {
  const uuid = useMemo(uuidV4, []);
  const [openTlp, setOpenTlp] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocumentClick = (event: Event) => {
      if (
        event.target instanceof Node &&
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpenTlp(false);
      }
    };

    if (openTlp) {
      document.addEventListener('click', onDocumentClick);
    }

    return () => {
      if (openTlp) {
        document.removeEventListener('click', onDocumentClick);
      }
    };
  }, [openTlp]);

  useEffect(() => {
    if (containerRef.current) {
      onMount?.(containerRef.current);
    }
  }, [onMount]);

  const title = useMemo(() => {
    if (text === null) {
      return null;
    }

    if (Array.isArray(text)) {
      return (
        <>
          {text.map((value) => {
            const elem = (
              <div
                className={styles.textElem}
                onClick={(event) => {
                  event.stopPropagation();
                  setOpenTlp(false);
                }}
                key={`tooltip-text-${uuid}-${value}`}
              >
                {value}
              </div>
            );

            if (withClipboard) {
              return (
                <ClipboardController
                  key={`cc-tooltip-text-${uuid}-${value}`}
                  text={value}
                >
                  {elem}
                </ClipboardController>
              );
            }

            return elem;
          })}
        </>
      );
    }

    const elem = (
      <div
        className={styles.textElem}
        onClick={(event) => {
          event.stopPropagation();
          setOpenTlp(false);
        }}
      >
        {text}
      </div>
    );

    if (withClipboard) {
      return <ClipboardController text={text}>{elem}</ClipboardController>;
    }

    return elem;
  }, [text, withClipboard, uuid]);

  return (
    <TooltipComponent
      title={disabled ? null : title}
      placement="top"
      arrow
      enterDelay={0}
      enterNextDelay={0}
      leaveTouchDelay={2500}
      open={openTlp}
      onOpen={() => {
        if (disabled) {
          return;
        }

        setOpenTlp(true);
      }}
      onClose={() => {
        setOpenTlp(false);
      }}
      classes={{
        tooltip: tooltipClassName ?? styles.tooltip,
        arrow: arrowClassName ?? styles.arrow,
      }}
      slotProps={{
        popper: {
          onClick: (event) => {
            event.stopPropagation();
          },
          sx: {
            [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
              {
                marginTop: '15px',
              },
            [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
              {
                marginBottom: '15px',
              },
            [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
              {
                marginLeft: '15px',
              },
            [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
              {
                marginRight: '15px',
              },
          },
        },
      }}
    >
      <div
        className={containerClassName}
        ref={containerRef}
        onClick={() => {
          if (disabled) {
            return;
          }

          setOpenTlp(true);
        }}
      >
        {children}
      </div>
    </TooltipComponent>
  );
};

export default Tooltip;
